<template>
  <div>
    <base-help-tooltip
      class="mb-5"
      popover-placement="top"
    >
      <template v-slot:left>
        <h3
          class="mb-0"
        >
          Display Content
        </h3>
      </template>
      <template v-slot:popover-body>
        <p
          class="display-content-tooltip mb-0"
        >
          Text entered here is used for display purposes only. It does not affect how your articles match with publisher articles.
        </p>
      </template>
    </base-help-tooltip>
    <b-form-group
      id="url"
      class="mb-5"
    >
      <template
        v-slot:label
        label-for="article-url-input"
      >
        <h4
          class="mb-0 form-label"
        >
          Landing Page URL
        </h4>
      </template>
      <b-input-group>
        <b-form-input
          id="article-url-input"
          class="border-right-0 pr-0"
          placeholder="display-content-url"
          :value="displayURL"
          data-test="article-url-input"
          disabled
        />
        <b-input-group-append
          class="m-0"
        >
          <b-button
            id="url-open-button"
            size="sm"
            variant="tertiary"
            class="m-0 border-left-0 btn-link"
            @click="openURLInNewTab(displayURL)"
          >
            <i class="fal fa-arrow-up-right-from-square" />
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>
    <b-form-group
      id="display-content-headline-input"
      class="mb-5"
    >
      <template
        v-slot:label
        label-for="display-content-headline-input"
      >
        <h4
          class="mb-0 form-label"
        >
          Display Headline
        </h4>
      </template>
      <b-form-input
        id="display-content-headline-input"
        placeholder="Display Content Headline"
        :value="displayHeadline"
        @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
        @blur="onBlur('title', $event)"
        @keydown.enter.prevent="updateDisplayContent('title', $event)"
        :disabled="GET_IS_CAMPAIGN_READONLY"
        data-test="display-content-headline-input"
      />
    </b-form-group>
    <b-form-group
      id="display-content-text-input"
      class="mb-5"
    >
      <template
        v-slot:label
        label-for="display-content-text-input"
      >
        <h4
          class="mb-0 form-label"
        >
          Display Text
        </h4>
      </template>
      <b-form-textarea
        id="display-content-text-input"
        placeholder="Display Content Text"
        :value="displayText"
        @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
        @blur="onBlur('content', $event)"
        @keydown.enter="updateDisplayContent('content', $event)"
        rows="12"
        max-rows="12"
        no-resize
        :disabled="GET_IS_CAMPAIGN_READONLY"
        data-test="display-content-text-input"
      />
      <p class="mb-0 mt-2">
        To add a new line <small><kbd class="tag-primary">SHIFT + ENTER</kbd></small>
      </p>
    </b-form-group>
    <b-form-group
      id="display-content-call-to-action-input"
    >
      <template
        v-slot:label
        label-for="display-content-call-to-action-input"
      >
        <h4
          class="mb-0 form-label"
        >
          Call to Action
        </h4>
      </template>
      <b-form-input
        id="display-content-call-to-action-input"
        placeholder="Call to Action"
        :value="displayCTA"
        @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
        @blur="onBlur('alternateCTA', $event)"
        @keydown.enter.prevent="updateDisplayContent('alternateCTA', $event)"
        :disabled="GET_IS_CAMPAIGN_READONLY"
        data-test="display-content-call-to-action-input"
      />
    </b-form-group>
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';

import textHelper from '@/core/mixins/textHelper';
import urlHelper from '@/core/mixins/urlHelper';

export default {
  name: 'DisplayContent',
  mixins: [
    textHelper,
    urlHelper,
  ],
  computed: {
    ...mapState('campaigns', [
      'selectedAdvertiser',
    ]),
    ...mapState('content', [
      'selectedArticleUuid',
      'selectedCreativeId',
      'feedId',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_CONTENT_ITEM_DISPLAY_PROPERTIES,
    ]),
    selectedCreativeTemplateProperties() {
      return this.GET_CONTENT_ITEM_DISPLAY_PROPERTIES(this.selectedArticleUuid, this.selectedCreativeId);
    },
    displayURL() {
      return this.selectedCreativeTemplateProperties?.url || '';
    },
    displayHeadline() {
      return this.selectedCreativeTemplateProperties?.title || '';
    },
    displayText() {
      return this.selectedCreativeTemplateProperties?.content || '';
    },
    displayCTA() {
      return this.selectedCreativeTemplateProperties?.alternateCTA || 'Read More';
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.UPDATE_CONTENT_DISPLAY_COPY,
      contentTypes.TRIGGER_CREATIVE_RERENDER,
      contentTypes.SET_IS_SIDEBAR_INPUT_FOCUSED,
    ]),
    onBlur(property, event) {
      this.SET_IS_SIDEBAR_INPUT_FOCUSED(false);
      this.updateDisplayContent(property, event);
    },
    updateDisplayContent(property, event) {
      if (this.GET_IS_CAMPAIGN_READONLY) return;
      const computed = {
        title: 'displayHeadline',
        content: 'displayText',
        alternateCTA: 'displayCTA',
      };

      if (event.type === 'keydown') {
        if (event.shiftKey || event.altKey || event.ctrlKey) return;
        event.preventDefault();
      }

      const newVal = event.target.value;
      if (!newVal) {
        event.target.value = this[computed[property]];
        return;
      }
      if (this.isEqualString(newVal, this[computed[property]])) return;
      const contentPayload = {
        creativeId: this.selectedCreativeId,
        advertisersId: this.selectedAdvertiser.id,
        feedId: this.feedId,
        id: this.selectedArticleUuid,
        body: {
          [property]: newVal,
        },
      };
      this.UPDATE_CONTENT_DISPLAY_COPY(contentPayload)
        .then(() => {
          this.TRIGGER_CREATIVE_RERENDER();
        });
    },
  },
};
</script>
<style scoped lang="scss">
#url-open-button {
  background-color: $gray-100;
  &:hover, &:focus {
    background-color: $gray-200;
  }
}
</style>
